import { useLayoutEffect, useRef, useState } from 'react'
import Select, { GroupBase, StylesConfig } from 'react-select'
import CreatableSelect from 'react-select/creatable'

import PropTypes from 'prop-types'

import { useSelectSearchable } from './useSelectSearchable'

type SelectOption = { value: string | number; label: string | number }

const CustomOption = props => {
  const { data, isSelected, isFocused, isDisabled, innerRef, innerProps } =
    props
  const { optionStyles, OptionIcon } = useSelectSearchable({
    isDisabled,
    isSelected,
    isFocused
  })

  return (
    <div ref={innerRef} {...innerProps} style={optionStyles}>
      {OptionIcon}
      <span>{data.label}</span>
    </div>
  )
}

/**
 * @component
 * @example
 *    const options = [{value: 1, label: 'Label do item'}, {value: 2, label: 'Label do item 2'}];
 *
 *    <SelectSearchable
 *      placeholder="Filtrar por locais"
 *      options={locations}
 *      loading={loading}
 *      onChange={handleChangeLocations}
 *      defaultValue={filteredLocations}
 *    />
 *
 * @param {array} options Objeto com itens que serão as opções do select. {value: 1, label: 'Label do item'}
 * @param {array} defaultValue Objeto com itens já selecionados. Mesmo formato do options.
 * @param {function} onChange Função chamada a cada mudança no select. Retorna um array com os objetos selecionados
 * @param {string} placeholder Texto para o placeholder. Default: "Selecione".
 * @param {boolean} loading Status de carregamento. Default: false.
 * @param {string} error Erro ao carregar itens para lista. Default: null.
 * @param {boolean} creatable Aceita um valor digitado que não está nas opções. Default: false.
 *
 */

const SelectSearchable = ({
  options,
  onChange,
  placeholder,
  loading,
  error,
  defaultValue,
  isClearable,
  styles = {} as StylesConfig<SelectOption, boolean, GroupBase<SelectOption>>,
  isMulti = false,
  creatable = false,
  selectRef,
  ...rest
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement | null>(
    null
  )

  useLayoutEffect(() => {
    const dialog = containerRef.current?.closest('dialog') as HTMLElement | null
    setMenuPortalTarget(dialog ?? document.body)
  }, [])

  const customControl = state => ({
    display: 'inline-flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 'var(--sl-space-3)',
    color: 'var(--sl-fg-base-soft)',
    font: 'var(--sl-text-body-font)',
    letterSpacing: 'var(--sl-text-body-letter-spacing)',
    border: state.isFocused
      ? 'var(--sl-border-base-strong-hover)'
      : 'var(--sl-border-base-strong)',
    borderRadius: 'var(--sl-radius-2)',
    background: 'var(--sl-bg-base)',
    outline: 'none',
    appearance: 'none',
    minWidth: '13rem',
    width: '100%',
    height: '2.75rem',
    boxSizing: 'border-box',
    boxShadow: state.isFocused ? 'var(--sl-focus-ring-base)' : null,
    '&:hover': {
      border: 'var(--sl-border-base-strong-hover)'
    },
    '&:disabled': {
      opacity: 1,
      cursor: 'auto',
      border: 'var(--sl-border-base-disabled)',
      color: 'var(--sl-fg-base-disabled)',
      background: 'var(--sl-bg-base-disabled)'
    }
  })

  const customStyles = {
    valueContainer: base => ({ ...base }),
    placeholder: provided => ({
      ...provided,
      lineHeight: 1,
      color: 'var(--sl-color-gray-6)'
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    menu: base => ({
      ...base,
      padding: '8px',
      zIndex: 9999
    }),
    ...styles,
    control: (base, state) => ({
      ...base,
      ...customControl(state),
      ...styles?.control?.(base, state)
    })
  }

  const multiStyles = {
    ...customStyles,
    control: (base, state) => ({
      ...base,
      ...customControl(state)
    }),
    valueContainer: provided => ({
      ...provided,
      lineHeight: 1,
      fontSize: '.75rem'
    })
  }

  const SelectComponent = creatable ? CreatableSelect : Select

  return (
    <div ref={containerRef} style={{ display: 'contents' }}>
      <SelectComponent
        closeMenuOnSelect={!isMulti}
        isMulti={isMulti}
        options={options}
        placeholder={placeholder}
        onChange={onChange}
        isLoading={loading}
        isDisabled={!options || error || rest?.disabled}
        defaultValue={defaultValue}
        classNamePrefix="SelectSearchable"
        openMenuOnFocus
        openMenuOnClick
        menuPosition="fixed"
        menuPortalTarget={menuPortalTarget}
        styles={isMulti ? multiStyles : customStyles}
        isClearable={isClearable}
        components={{ ...rest?.components, Option: CustomOption }}
        {...(selectRef ? { ref: selectRef } : {})}
        {...rest}
      />
    </div>
  )
}

export default SelectSearchable

SelectSearchable.propTypes = {
  styles: PropTypes.shape({}),
  selectRef: PropTypes.shape({}),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  loading: PropTypes.bool,
  isClearable: PropTypes.bool,
  isMulti: PropTypes.bool,
  creatable: PropTypes.bool,
  error: PropTypes.string,
  defaultValue: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  )
}

SelectSearchable.defaultProps = {
  styles: {},
  onChange: null,
  selectRef: null,
  options: null,
  placeholder: 'Selecione',
  loading: false,
  error: null,
  defaultValue: null,
  isClearable: true,
  isMulti: false,
  creatable: false
}
